import { html, type Html, on, Page } from 'rune-ts';
import klass from './AdResAd.module.scss';
import { isNull, pipe, throwIf } from '@fxts/core';
import { LayoutView } from '../../components/cells/Layout/LayoutView';
import { getResizedUrl } from '../../../shared/util/image';
import { BaseLayoutOptions } from '../../../features/Core/service';
import { footer_links } from '../../components/cells/Footer/constant';
import { postApplySellerApi } from '../../../features/Creator/api';
import { loading, loadingEnd } from '../../../shared/client/global';
import { Confirm } from '../../components/cells/Confirm/Confirm';
import { CreatorInputText } from '../AdRes/views/CreatorInputText';
import { CreatorInputTextArea } from '../AdRes/views/CreatorInputTextArea';
import { CreatorInputFile, FileChangeEvent } from '../AdRes/views/CreatorInputFile';
import { CreatorCheckBoxes, OpenTermsModalEvent } from '../AdRes/views/CreatorCheckBoxes';
import { TermsModalBody } from '../AdRes/views/TermsModal/TermsModalBody';
import { TermsModalButton } from '../AdRes/views/TermsModal/TermsModalButton';
import { EventBanner } from './views/EventBanner';
import { SpecialKeyValidator } from '../AdRes/validate/validators';
import { SellerTypeFilters } from '../../../entities/pool/public/seller_type_filters';
import { Alert } from '../../components/cells/Alert/Alert';
import { staticTypoPcMo, typo, typoPcMo } from '../../../shared/typography/typo';
import { ImportantIcon, MarkIcon, ListMarkIcon } from '../../../shared/components/atoms/Icon';
import { MShopAppAdResCookieOvenF } from '../../../../../modules/MShop/App/AdRes/CookieOven/F/Function/module/MShopAppAdResCookieOvenF';
import { AdScriptGaF } from '../../../../../modules/AdScript/Ga/F/Function/module/AdScriptGaF';
import {
  checkSellerDomainLength,
  is_number,
  is_special_str,
  is_valid_email,
  isEnOrNumberOrUnder,
  isKrPhoneNumber,
  isUrl,
} from '@modules/Util/S/Function/util';
import { Modal } from '../../components/atoms/Modal/Modal';
import { InputResetEvent } from '../../components/atoms/Input/InputEvent';
import { font } from '../../../shared/typography';
import { CreatorCategorySelect } from '../AdRes/views/CreatorCategorySelect';

export interface AdResPageData {
  layout_options: BaseLayoutOptions;
  seller_types: SellerTypeFilters[];
  selected_category?: string;
}

export class AdResAdPage extends Page<AdResPageData> {
  seller_name_input = new CreatorInputText({
    name: 'seller_name',
    title: ET('mps2::ad_res::brand_name'),
    required: true,
    type: 'text',
    custom_klass: 'regex',
    placeholder: ET('mps2::ad_res::brand_placeholder'),
    maxlength: 20,
    text_helper: ET('mps2::ad_res::brand_validation'),
    error_helper: [
      {
        message: ET('mps2::ad_res::brand_validation'),
        isError: (text) => {
          return text.length < 2 || text.length > 20;
        },
      },
      {
        message: ET('mps2::ad_res::brand_validation_error'),
        isError: (text) => {
          const reg = /^[가-힣ㄱ-ㅎㅏ-ㅣa-zA-Z0-9]+$/;
          return !reg.test(text);
        },
      },
    ],
    error_helper_mode: 'single',
    show_error_mobile: true,
  });

  domain_name_input = new CreatorInputText({
    name: 'domain_name',
    title: ET('mps2::ad_res::domain_name'),
    required: true,
    type: 'text',
    custom_klass: 'domain_name_input',
    placeholder: ET('mps2::ad_res::domain_placeholder'),
    maxlength: 20,
    text_helper: ET('mps2::ad_res::domain_validation'),
    error_helper: {
      message: ET('mps2::ad_res::domain_validation_error'),
      isError: (text) => {
        const name = text.trim().toLowerCase();
        const reg = /^[a-zA-Z0-9|_]+$/;
        return name.length < 2 || name.length > 20 || !reg.test(name);
      },
    },
    show_error_mobile: true,
  });

  email_input = new CreatorInputText({
    name: 'email',
    title: ET('mps2::ad_res::email_name'),
    required: true,
    type: 'text',
    custom_klass: 'email_input',
    placeholder: ET('mps2::ad_res::email_placeholder'),
    error_helper: {
      message: ET('mps2::ad_res::email_error'),
      isError: (text) => {
        const email = text.trim().toLowerCase();
        return Boolean(email && !is_valid_email(email));
      },
    },
    error_helper_mode: 'single',
    show_error_mobile: true,
  });

  mobile_input = new CreatorInputText({
    name: 'mobile',
    title: ET('mps2::ad_res::phone'),
    required: true,
    type: 'text',
    placeholder: ET('mps2::ad_res::phone_placeholder'),
    maxlength: 20,
    error_helper: {
      message: ET('mps2::ad_res::phone_error'),
      isError: (text) => {
        const mobile = text.trim().toLowerCase();
        if (mobile && ET.lang === 'kr' && !isKrPhoneNumber(mobile)) return true;
        return !!(mobile && ET.lang !== 'kr' && !is_number(text));
      },
    },
    show_error_mobile: true,
  });

  sns_url_input = new CreatorInputText({
    name: 'sns_url',
    title: ET('mps2::ad_res::sns'),
    type: 'text',
    placeholder: ET('mps2::ad_res::sns_placeholder'),
    error_helper: {
      message: ET('mps2::ad_res::url_error'),
      isError: (text) => {
        const url = text.trim();
        return Boolean(url && !isUrl(url));
      },
    },
    show_error_mobile: true,
  });

  sample_url_input = new CreatorInputText({
    name: 'sample_url',
    title: ET('mps2::ad_res::website'),
    type: 'text',
    placeholder: ET(`mps2::ad_res::${this.data.layout_options.is_mobile ? 'mo_' : ''}website_placeholder`),
    error_helper: {
      message: ET('mps2::ad_res::url_error'),
      isError: (text) => {
        const url = text.trim();
        return Boolean(url && !isUrl(url));
      },
    },
    show_error_mobile: true,
  });

  seller_type_filter_input = new CreatorCategorySelect({
    seller_types: this.data.seller_types,
    selected_category: this.data.selected_category,
  });

  textarea_input = new CreatorInputTextArea();
  file_input = new CreatorInputFile();
  check_input = new CreatorCheckBoxes();
  terms_modal_body = new TermsModalBody();
  terms_modal_button = new TermsModalButton();
  terms_modal = new Modal({
    title: ET('mps2::ad_res::terms_modal::title'),
    size: this.data.layout_options.is_mobile ? 'large' : 'free',
    isOpen: false,
    backdrop: true,
    body: this.terms_modal_body,
    footer: this.terms_modal_button,
    container_klass: klass.outer_modal,
    enable_app_bottom_padding: true,
  });

  protected override template({ layout_options }: AdResPageData): Html {
    const { is_mobile } = layout_options;
    const background_url = is_mobile
      ? '//s3.marpple.co/files/u_4648126/2025/3/original/6ab7915caed0d8939753ea79ba3f219456dae31b1.png'
      : '//s3.marpple.co/files/u_4648126/2025/3/original/c5ec212b69a3dd13ee810b98daaa5375daab3b671.png';
    const body = html`
      <div class="${klass.page}">
        ${ET.lang === 'kr'
          ? html`
              ${is_mobile
                ? html`
                    <img
                      class="${klass.creative_creator}"
                      src="${getResizedUrl({
                        url: '//s3.marpple.co/files/u_4648126/2025/1/original/e900409f560d3957c5eab61687265b0d3cea707e10.webp',
                        format: 'webp',
                        quality: 85,
                      })}"
                      alt="크리에이티브 크리에이터 아이콘"
                    />
                    <img
                      class="${klass.letter_balloon}"
                      src="${getResizedUrl({
                        url: '//s3.marpple.co/files/u_4648126/2025/1/original/66b212e26613917c0e92759dc40393997e681e2c41.webp',
                        format: 'webp',
                        quality: 85,
                      })}"
                      alt="M 레터 풍선 아이콘"
                    />
                  `
                : html`
                    <div class="${klass.background}">
                      <img
                        src="${getResizedUrl({
                          url: background_url,
                          width: is_mobile ? 375 * 2 : 1920 * 2,
                          quality: 85,
                          format: 'webp',
                        })}"
                        alt="크리에이터 인터뷰 이미지"
                      />
                    </div>
                  `}
              <div class="${klass.ad_info}">
                <div class="${klass.title_wrapper}">
                  <div class="${klass.title} ${font('48_bold')}">
                    마플샵은 <br />
                    판매수수료 0%
                  </div>
                  <div class="${klass.sub_title} ${font('16_medium')}">*타사 플랫폼 수수료 5~20%</div>
                </div>
                <video
                  autoplay=""
                  loop=""
                  muted=""
                  playsinline=""
                  class="${klass.motion_video}"
                  src="https://s3.ap-northeast-2.amazonaws.com/s3.marpple.co/files/u_4648126/2025/3/original/creator_apply_ad.mp4"
                ></video>
                <div class="${klass.description} ${font('40_bold', '24_bold')}">
                  직접 만들어서 파세요? <br />
                  그럼 수수료 내지 마세요
                </div>
              </div>

              <!--핸드메이드 고객 수수료 이벤트 공지(간소화)-->
              ${new EventBanner()}

              <div class="${klass.ad_sub_info}">
                <div class="${font('32_bold', '24_bold')}">
                  크리에이터 신청하기 <br />
                  3분이면 완료
                </div>
                <div class="${font('16_medium')}">
                  추후 수정 가능해요 <br />
                  빨리 신청하고 크리에이터 승인 받으세요!
                </div>
              </div>
            `
          : html`
              <div class="${klass.background}">
                <img
                  src="${getResizedUrl({
                    url: background_url,
                    width: is_mobile ? 375 * 2 : 1920 * 2,
                    quality: 85,
                    format: 'webp',
                  })}"
                  alt="크리에이터 인터뷰 이미지"
                />
              </div>
              <div class="${klass.info}">
                <div class="${klass.title_wrapper}">
                  <div
                    class="${klass.title} ${staticTypoPcMo({
                      is_mobile,
                      pc: 'bebas_20_bold',
                      mo: 'bebas_16_bold',
                    })}"
                  >
                    MAKE IT REAL
                  </div>
                  <div class="${typoPcMo({ is_mobile, pc: '36_bold', mo: '24_bold' })}">
                    ${ET('mps2::ad_res::title')}
                  </div>
                </div>
                <div class="${klass.description_wrapper}">
                  <div class="${typoPcMo({ is_mobile, pc: '16_medium', mo: '14_medium' })}">
                    ${html.preventEscape(ET('mps2::ad_res::description_1'))}
                  </div>
                  <div class="${typoPcMo({ is_mobile, pc: '16_medium', mo: '14_medium' })}">
                    ${is_mobile
                      ? html.preventEscape(ET('mps2::ad_res::mo_description_2'))
                      : html.preventEscape(ET('mps2::ad_res::description_2'))}
                  </div>
                </div>
              </div>
            `}

        <div class="${klass.ad_res}">
          <div class="${klass.necessary} ${typo('14_medium')}">
            <div class="${klass.mark}">${MarkIcon}</div>
            <div>${ET('mps2::ad_res::required')}</div>
          </div>

          <form class="${klass.form}" name="adResForm">
            <div class="${klass.fieldset}">
              <legend>
                <p class="${typoPcMo({ is_mobile, pc: '20_bold', mo: '16_bold' })}">
                  ${ET('mps2::ad_res::basic_info')}
                </p>
              </legend>

              <!--    샵 이름-->
              ${this.seller_name_input}

              <!--    도메인명-->
              ${this.domain_name_input}

              <!--    카테고리-->
              ${this.seller_type_filter_input}

              <!--    소개글-->
              ${this.textarea_input}
            </div>

            <div class="${klass.fieldset}">
              <legend>
                <p class="${typoPcMo({ is_mobile, pc: '20_bold', mo: '16_bold' })}">
                  ${ET('mps2::ad_res::contact_info')}
                </p>
              </legend>

              <!--    이메일-->
              ${this.email_input}

              <!--    모바일-->
              ${this.mobile_input}
            </div>

            <div class="${klass.fieldset}">
              <div class="${klass.necessary} ${klass.necessary_info} ${typo('14_medium')}">
                <div class="${klass.mark}">${MarkIcon}</div>
                <div>${ET('mps2::ad_res::required_one_optional')}</div>
              </div>
              <legend>
                <p class="${typoPcMo({ is_mobile, pc: '20_bold', mo: '16_bold' })}">
                  ${ET('mps2::ad_res::portfolio')}
                </p>
                <p class="${klass.portfolio_desc} ${font('12_medium')}">
                  ${ET('mps2::ad_res::portfolio_sub')}
                </p>
              </legend>
              <!--    포폴-->
              ${this.sns_url_input}

              <!--    참고사이트-->
              ${this.sample_url_input}

              <!--    파일-->
              ${this.file_input}
            </div>

            <div class="${klass.fieldset}">
              <legend>
                <p class="${typoPcMo({ is_mobile, pc: '20_bold', mo: '16_bold' })}">
                  ${ET('mps2::ad_res::agreement')}
                </p>
              </legend>
              <!--    체크박스-->
              ${this.check_input}

              <div class="${klass.noti} ">
                <div class="${klass.noti_title}">${ImportantIcon} ${ET('mps2::ad_res::notification')}</div>
                <ul class="${klass.noti_list}">
                  <li class="${klass.noti_list_item}">
                    <div class="${klass.bullet}">${ListMarkIcon}</div>
                    <div class="${typoPcMo({ is_mobile, pc: '14_medium', mo: '12_medium' })}">
                      ${html.preventEscape(ET('mps2::ad_res::notification_1'))}
                    </div>
                  </li>
                  <li class="${klass.noti_list_item}">
                    <div class="${klass.bullet}">${ListMarkIcon}</div>
                    <div class="${typoPcMo({ is_mobile, pc: '14_medium', mo: '12_medium' })}">
                      ${ET('mps2::ad_res::notification_2')}
                    </div>
                  </li>
                  <li class="${klass.noti_list_item}">
                    <div class="${klass.bullet}">${ListMarkIcon}</div>
                    <div class="${typoPcMo({ is_mobile, pc: '14_medium', mo: '12_medium' })}">
                      ${ET('mps2::ad_res::notification_3')}
                    </div>
                  </li>
                </ul>
              </div>
            </div>

            <div class="${klass.form_submit} ${klass.disabled}">
              <button type="submit" class="${klass.form_submit_btn} ${typo('16_bold')}">
                ${ET('mps2::ad_res::apply')}
              </button>
            </div>
          </form>
        </div>
        ${this.terms_modal}
      </div>
    `;

    return html`
      <div>
        ${new LayoutView(
          {
            body,
            footer_data: { ...footer_links, lang: ET.lang, is_mobile },
          },
          {
            require_top_arrow_button: false,
            full_width: true,
            has_bottom_tab_bar: false,
            selected_bottom_tab_idx: -1,
            ...layout_options,
          },
        )}
      </div>
    `;
  }

  override onRender() {
    box.set('ad_res', { name: false, files: true });
    this.delegate('click', TermsModalButton, () => {
      this.terms_modal.close();
    });

    this.addEventListener(FileChangeEvent, () => {
      this.checkAllValid();
    });

    this.addEventListener(InputResetEvent, () => {
      this.checkAllValid();
    });

    this.addEventListener(OpenTermsModalEvent, () => {
      this.terms_modal.open();
    });
  }

  @on('keydown', '.regex')
  keyDownCheckRegex(e: KeyboardEvent & { originalEvent: KeyboardEvent }) {
    const key = e.key;
    const result = SpecialKeyValidator.safeParse(key);
    if (key && key.length === 1 && !result.success) {
      e.originalEvent.preventDefault();
    }
  }

  @on('change', '[name]')
  @on('click', '.creator_category_list')
  @on('focusout', 'textarea[name="description"]')
  @on('change', '[type="checkbox"]')
  checkAllValid() {
    const $submit_btn = document.querySelector(`.${klass.form_submit}`) as HTMLButtonElement;

    if (
      this.seller_name_input.isValidate() &&
      this.domain_name_input.isValidate() &&
      this.seller_type_filter_input.isValidate() &&
      this.textarea_input.isValidate() &&
      (this.sns_url_input.isValidate() ||
        this.sample_url_input.isValidate() ||
        this.file_input.isValidate()) &&
      this.email_input.isValidate() &&
      this.mobile_input.isValidate() &&
      this.check_input.isValidate()
    ) {
      $submit_btn.classList.remove(`${klass.disabled}`);
    } else {
      $submit_btn.classList.add(`${klass.disabled}`);
    }
  }

  @on('click', `.${klass.form_submit}`)
  async submitForm(e: MouseEvent & { originalEvent: MouseEvent }) {
    e.originalEvent.preventDefault();

    const cookie_oven_click_key = MShopAppAdResCookieOvenF.parseClickKeyFromStorage();

    const data = {
      seller_name: this.seller_name_input.getValue().trim(),
      name: this.domain_name_input.getValue(),
      description: this.textarea_input.getValue().trim(),
      email: this.email_input.getValue().trim(),
      mobile: this.mobile_input.getValue().trim(),
      sns_url: this.sns_url_input.getValue().trim(),
      sample_url: this.sample_url_input.getValue().trim(),
      files: this.file_input.getValue(),
      cookie_oven_click_key,
      seller_type_filter_id: this.seller_type_filter_input.getSellerFilterTypeId(),
    };

    if (!data.seller_name || is_special_str(data.seller_name) || !this.seller_name_input.isValidate()) {
      return this.focusElementBySelector('[name=seller_name]');
    } else if (
      !checkSellerDomainLength(data.name) ||
      !isEnOrNumberOrUnder(data.name) ||
      !this.domain_name_input.isValidate()
    ) {
      return this.focusElementBySelector('input[name="domain_name"]');
    } else if (!data.seller_type_filter_id) {
      return this.focusElementBySelector('.creator_category_list');
    } else if (!data.description) {
      return this.focusElementBySelector('textarea[name="description"]');
    } else if (!data.email || !is_valid_email(data.email) || !this.email_input.isValidate()) {
      return this.focusElementBySelector('input[name="email"]');
    } else if (!data.mobile || !is_number(data.mobile) || !this.mobile_input.isValidate()) {
      return this.focusElementBySelector('input[name="mobile"]');
    } else if (
      !(this.sns_url_input.isValidate() || this.sample_url_input.isValidate() || this.file_input.isValidate())
    ) {
      return this.focusElementBySelector('input[name="sns_url"]');
    } else if (!this.element().querySelector<HTMLInputElement>('input[name="terms"]')?.checked) {
      return Alert.open({
        title: ET('mps2::ad_res::agree_personal'),
        confirmText: ET('mps2::ad_res::file_upload_confirm'),
      });
    } else if (!this.element().querySelector<HTMLInputElement>('input[name="privacy"]')?.checked) {
      return Alert.open({
        title: ET('mps2::ad_res::agree_age'),
        confirmText: ET('mps2::ad_res::file_upload_confirm'),
      });
    } else if (!box.sel('ad_res->files')) {
      return Alert.open({
        title: ET('mps2::ad_res::file_uploading'),
        confirmText: ET('mps2::ad_res::file_upload_confirm'),
      });
    }

    const confirm = await Confirm.open({
      title: ET('mps2::ad_res::creator_apply'),
      message: html`
        <div class="${klass.confirm}">
          <div class="${klass.title}">${html.preventEscape(ET('mps2::ad_res::apply_desc'))}</div>
          <div class="${klass.content}">
            <div class="${klass.phone}">${data.mobile}</div>
            <div class="${klass.email}">${data.email}</div>
          </div>
        </div>
      `,
      confirmText: ET('mps2::ad_res::apply'),
      cancelText: ET('mps2::ad_res::apply_cancel'),
    });

    if (!confirm) return;

    loading();
    const { result, c_id, msg } = await postApplySellerApi(data);
    loadingEnd();
    if (result) {
      AdScriptGaF.applyCreator();
      if (cookie_oven_click_key) MShopAppAdResCookieOvenF.removeCookieOvenData();
      location.replace(`/${T.lang}/@/advance_reservation_finish?c_id=${c_id}`);
    } else {
      if (msg == '같은 이메일로 이미 신청되었습니다.') {
        await Alert.open({
          message: html.preventEscape(ET('mps2::ad_res::fail_same_email')),
          confirmText: ET('mps2::ad_res::file_upload_confirm'),
        });
      } else {
        await Alert.open({
          message: html.preventEscape(ET('mps2::ad_res::fail_apply')),
          confirmText: ET('mps2::ad_res::file_upload_confirm'),
        });
      }
    }
  }

  focusElementBySelector(selector: string) {
    return pipe(this.element().querySelector<HTMLElement>(selector), throwIf(isNull)).focus();
  }
}
